import { useEffect, useMemo, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { LuLoaderCircle } from "react-icons/lu";
import dayjs from "dayjs";

import Button from "../ui/Button";
import ConfirmModal from "./ConfirmModal";
import { Input } from "../ui/shadcn/input";
import { Checkbox } from "../ui/shadcn/checkbox";
import { useShow } from "@/contexts/ShowContext";
import { useModal } from "@/contexts/ModalContext";

const DEFAULT_FORMAT =
  "{{NAME}} ({{FF}}) {{####}} {{MODEL}}/{{MEDIA}} - {{TIME}}";
const PREVIEW_DEBOUNCE_MS = 400;

// Mirrors the patterns the backend substitutes (see api lib/BulkRename/
// formatPatterns.ts). A format with none of these renames every asset to the
// same literal string, so at least one is required before renaming is allowed.
const FORMAT_TOKEN_PATTERN = /\{\{(NAME|MODEL|MEDIA|TIME|F+|#+)\}\}/;

const FORMAT_TOKENS = [
  { label: "Name", token: "{{NAME}}" },
  { label: "Parent folder", token: "{{F}}" },
  { label: "Counter", token: "{{####}}" },
  { label: "Model", token: "{{MODEL}}" },
  { label: "Media", token: "{{MEDIA}}" },
  { label: "Time", token: "{{TIME}}" },
];

export default function BulkRenameModal({ onClose, assetIds = [] }) {
  const { previewBulkRename } = useShow();
  const { openModal } = useModal();

  const [format, setFormat] = useState("");
  const [startIndex, setStartIndex] = useState(1);
  const [time, setTime] = useState(() => dayjs().format("HH:mm:ss"));
  const [noTime, setNoTime] = useState(false);

  const [renames, setRenames] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const requestIdRef = useRef(0);
  const inputRef = useRef(null);

  const count = assetIds.length;

  const insertToken = (token) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? format.length;
    const end = input?.selectionEnd ?? format.length;
    const next = format.slice(0, start) + token + format.slice(end);
    setFormat(next);
    const caret = start + token.length;
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(caret, caret);
      }
    });
  };

  const handleClear = () => {
    setFormat("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const buildTime = () => {
    if (noTime) return null;
    const [h, m, s] = time.split(":").map(Number);
    const d = new Date();
    d.setHours(h || 0, m || 0, s || 0, 0);
    return d.toISOString();
  };

  const args = useMemo(
    () => ({
      assetIds,
      format,
      startIndex: Number(startIndex) || 1,
      time: buildTime(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [assetIds, format, startIndex, time, noTime],
  );

  useEffect(() => {
    if (count === 0 || format.trim() === "") {
      setRenames([]);
      setPreviewError(null);
      return;
    }
    const handle = setTimeout(async () => {
      const reqId = ++requestIdRef.current;
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await previewBulkRename(args);
        if (reqId !== requestIdRef.current) return;
        setRenames(result?.renames || []);
      } catch (error) {
        if (reqId !== requestIdRef.current) return;
        console.error("Failed to preview bulk rename", error);
        setRenames([]);
        setPreviewError("Failed to preview name changes");
      } finally {
        if (reqId === requestIdRef.current) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args]);

  const canApply =
    count > 0 && format.trim() !== "" && FORMAT_TOKEN_PATTERN.test(format);

  const handleApply = async () => {
    if (!canApply) return;
    const confirmed = await openModal(
      <ConfirmModal
        title="Bulk Rename Assets"
        message={`You are about to rename ${count} asset${count === 1 ? "" : "s"} - Are you sure you want to continue?`}
      />,
    );
    if (!confirmed) return;
    onClose({ format, startIndex: Number(startIndex) || 1, time: buildTime() });
  };

  return (
    <div
      id="BulkRenameModal"
      className="w-full h-full max-h-[640px] max-w-[560px] flex flex-col bg-background rounded-lg border">
      <div className="flex justify-between py-3 px-4 border-b items-center">
        <span className="text-white text-lg font-bold">Bulk Rename Assets</span>
        <div
          className="cursor-pointer text-gray-400 hover:text-white"
          onClick={() => onClose(null)}>
          <FiX className="w-6 h-6" />
        </div>
      </div>

      <div className="flex-1 overflow-hidden p-4 flex flex-col gap-4">
        <div className="text-[11px] text-secondary-foreground">
          {`Renaming ${count} asset${count === 1 ? "" : "s"}`}
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="bulk-rename-format"
            className="text-xs font-medium text-secondary-foreground">
            Format
          </label>
          <div className="relative">
            <Input
              ref={inputRef}
              id="bulk-rename-format"
              type="text"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              placeholder={DEFAULT_FORMAT}
              className="font-mono text-xs md:text-xs pr-9 placeholder:text-secondary-foreground/40"
            />
            {format !== "" && (
              <button
                type="button"
                title="Clear format"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-white cursor-pointer">
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
          <label
            htmlFor="bulk-rename-format"
            className="text-xs font-medium text-secondary-foreground mt-1">
            Insert
          </label>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {FORMAT_TOKENS.map((t) => (
              <button
                key={t.token}
                type="button"
                title={t.token}
                onClick={() => insertToken(t.token)}
                className="text-xs px-2 py-1 rounded-md border bg-background text-secondary-foreground hover:bg-secondary cursor-pointer">
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-end gap-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="bulk-rename-start"
              className="text-xs font-medium text-secondary-foreground">
              Start numbers at
            </label>
            <Input
              id="bulk-rename-start"
              type="number"
              value={startIndex}
              onChange={(e) => setStartIndex(e.target.value)}
              className="w-32"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="bulk-rename-time"
              className="text-xs font-medium text-secondary-foreground">
              Time
            </label>
            <Input
              id="bulk-rename-time"
              type="time"
              step="1"
              value={time}
              disabled={noTime}
              onChange={(e) => setTime(e.target.value)}
              className="w-40 appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="bulk-rename-no-time"
            checked={noTime}
            onCheckedChange={(checked) => setNoTime(checked === true)}
          />
          <label
            htmlFor="bulk-rename-no-time"
            className="text-xs text-secondary-foreground select-none cursor-pointer">
            Don&apos;t send a time (use current time when applied)
          </label>
        </div>

        <div className="flex flex-col gap-1 flex-1 min-h-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-secondary-foreground">
              Preview
            </span>
            {previewLoading && (
              <LuLoaderCircle className="w-3 h-3 animate-spin text-secondary-foreground" />
            )}
          </div>
          <div className="flex-1 overflow-y-auto rounded-md border bg-card/40 p-2">
            {previewError ? (
              <div className="text-xs text-destructive">{previewError}</div>
            ) : renames.length === 0 ? (
              <div className="text-xs text-secondary-foreground/60">
                {previewLoading ? "Computing preview…" : "No preview available"}
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {renames.map((r) => (
                  <li
                    key={r.assetId}
                    className="flex flex-col gap-0.5 text-xs leading-tight">
                    <span className="text-secondary-foreground/60 truncate">
                      {r.originalName}
                    </span>
                    <span className="font-semibold text-foreground truncate">
                      {r.reformattedName}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end p-4 pt-2 gap-3 border-t">
        <Button
          type="danger-ghost"
          className="min-w-20"
          onClick={() => onClose(null)}>
          Cancel
        </Button>
        <Button
          type="primary"
          className="min-w-20"
          onClick={handleApply}
          disabled={!canApply}>
          Rename
        </Button>
      </div>
    </div>
  );
}
